import React, { useContext, useRef, useState } from "react";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import TranslateIcon from "@material-ui/icons/Translate";
import IconButton from "@material-ui/core/IconButton";
import {
  AccountCircle,
  Logout,
  Settings,
  Public,
  Groups,
  BadgeOutlined
} from "@mui/icons-material";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import Divider from "@material-ui/core/Divider";
import { Box, ListItemIcon, ListItemText } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { SettingsDialog } from "./components/SettingsDialog";
import { useSelector } from "react-redux";
import { StoresState } from "../../../store/stores/types";
import { AppState } from "../../../store";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../UserProvider";
import { Authenticated, hasRole } from "../../../models/User";
import { firebase } from "../../../firebase";
import useStyles from "../styles";
import { useI18n } from "../../../hooks/useI18n";

interface UserMenuActionsState {
  openStoreFilterDialog: boolean;
}

const UserMenuActionsDefaultState: UserMenuActionsState = {
  openStoreFilterDialog: false
};

export const UserMenu = ({ ...props }) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const user = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const [open, setOpen] = useState(false);
  const [userMenuActionsState, setUserMenuActionsState] = useState(
    UserMenuActionsDefaultState
  );

  const anchorRef = useRef<HTMLButtonElement>(null);

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  const { stores } = storesState;

  const logout = (event: React.MouseEvent<EventTarget>) => {
    handleClose(event);
    firebase.logOut();

    window.location.href = "/auth/logout";
  };

  const goToMarkets = (event: React.MouseEvent<EventTarget>) => {
    // handleClose(event);
    navigate("markets");
    // window.location.href = "/markets";
  };

  const openSettings = (event: React.MouseEvent<EventTarget>) => {
    setUserMenuActionsState({
      ...userMenuActionsState,
      openStoreFilterDialog: true
    });
  };

  const closeSettings = () => {
    setUserMenuActionsState({
      ...userMenuActionsState,
      openStoreFilterDialog: false
    });
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <div style={{ display: "flex" }}>
      <div>
        <IconButton
          className={open ? classes.menuButtonActive : classes.menuButton}
          size="small"
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <AccountCircle />
        </IconButton>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          style={{ zIndex: 1 }}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom"
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <div>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        padding: "1rem"
                      }}
                    >
                      <Typography variant={"h6"}>{props.user.name}</Typography>
                    </Box>
                    <Divider />
                    <MenuList
                      autoFocusItem={open}
                      id="menu-list-grow"
                      onKeyDown={handleListKeyDown}
                    >
                      {hasRole(user, ["Root"]) && (
                        <>
                          <MenuItem
                            onClick={e => {
                              handleClose(e);
                              navigate("tenants");
                            }}
                          >
                            <ListItemIcon>
                              <AccountTreeIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t("common.tenants")} />
                          </MenuItem>
                          <MenuItem
                            onClick={e => {
                              handleClose(e);
                              navigate("markets");
                            }}
                          >
                            <ListItemIcon>
                              <Public fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t("common.markets")} />
                          </MenuItem>
                          <MenuItem
                            onClick={e => {
                              handleClose(e);
                              navigate("personnel");
                            }}
                          >
                            <ListItemIcon>
                              <BadgeOutlined fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={t("common.personnel")} />
                          </MenuItem>
                          <MenuItem
                            onClick={e => {
                              handleClose(e);
                              navigate("translations");
                            }}
                          >
                            <ListItemIcon>
                              <TranslateIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={t("common.messages.appTranslations")}
                            />
                          </MenuItem>
                        </>
                      )}
                      {stores.length > 1 && (
                        <MenuItem onClick={openSettings}>
                          <ListItemIcon>
                            <Settings fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={t("common.settings")} />
                        </MenuItem>
                      )}
                      <MenuItem onClick={logout}>
                        <ListItemIcon>
                          <Logout fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t("common.actions.logout")} />
                      </MenuItem>
                    </MenuList>
                  </div>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
      <SettingsDialog
        opened={userMenuActionsState.openStoreFilterDialog}
        onClose={closeSettings}
        appBarStyle={props.dialogBarStyle}
      />
    </div>
  );
};
